import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import FormService from 'service/FormService';
import FormGroupService from 'service/FormGroupService';
import { FormListResp } from 'domain/payload/response/form/FormListResp';
import { FormGroupSaveReq } from 'domain/payload/request/form-group/FormGroupSaveReq';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ChildFormItem {
  tempId: string;
  childId: string;
  alias: string;
  sort: number;
}

interface SortableRowProps {
  id: string;
  index: number;
  child: ChildFormItem;
  availableForms: FormListResp[];
  onChildChange: (index: number, field: keyof ChildFormItem, value: any) => void;
  onRemoveChild: (index: number) => void;
}

const SortableChildRow: React.FC<SortableRowProps> = ({
  id,
  index,
  child,
  availableForms,
  onChildChange,
  onRemoveChild
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    backgroundColor: isDragging ? '#f8fafc' : 'white',
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 999 : 'auto'
  };

  return (
    <tr ref={setNodeRef} style={style} className="align-middle">
      <td className="text-center align-middle" style={{ width: '50px' }}>
        <span
          style={{ cursor: 'grab', padding: '4px 6px' }}
          className="text-muted d-inline-flex align-items-center"
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <i className="fa-solid fa-grip-vertical"></i>
        </span>
      </td>
      <td className="text-center align-middle fw-bold" style={{ width: '50px' }}>
        {index + 1}
      </td>
      <td>
        <MultiSelectDropdown
          single={true}
          options={availableForms.map(f => ({
            code: f.id,
            name: f.name
          }))}
          value={child.childId ? [child.childId] : []}
          onChange={selected => onChildChange(index, 'childId', selected[0] || '')}
          placeholder="-- Choose Form --"
          style={{ width: '100%' }}
        />
      </td>
      <td>
        <input
          type="text"
          className="form-control form-control-compact"
          placeholder="e.g. Tahap 1 - Data Toko"
          value={child.alias}
          onChange={e => onChildChange(index, 'alias', e.target.value)}
        />
      </td>
      <td className="text-center align-middle" style={{ width: '70px' }}>
        <button
          type="button"
          className="btn btn-outline-danger btn-small-compact"
          onClick={() => onRemoveChild(index)}
          title="Remove Item"
        >
          <i className="fa-solid fa-trash"></i>
        </button>
      </td>
    </tr>
  );
};

const FormGroupFactory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useSelector((state: any) => state.auth);

  const formService = FormService();
  const formGroupService = FormGroupService();

  const isEdit = Boolean(id && id !== '0');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [children, setChildren] = useState<ChildFormItem[]>([]);
  const [availableForms, setAvailableForms] = useState<FormListResp[]>([]);

  const [loadingDetail, setLoadingDetail] = useState(isEdit);
  const [loadingForms, setLoadingForms] = useState(true);
  const [saving, setSaving] = useState(false);

  const isLoadingPage = loadingDetail || loadingForms;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  );

  // Fetch available content forms for dropdown selection
  useEffect(() => {
    const fetchAvailableForms = async () => {
      const token = profile?.data?.token;
      if (!token) {
        setLoadingForms(false);
        return;
      }

      setLoadingForms(true);
      try {
        const res = await formService.formList(token, '', 1, 100, 'content');
        setAvailableForms(res.data ?? []);
      } catch (err) {
        console.error('Failed to fetch content forms:', err);
      } finally {
        setLoadingForms(false);
      }
    };

    fetchAvailableForms();
  }, []);

  // Fetch detail if editing
  useEffect(() => {
    const fetchDetail = async () => {
      if (!isEdit || !id) {
        setLoadingDetail(false);
        return;
      }
      const token = profile?.data?.token;
      if (!token) {
        setLoadingDetail(false);
        return;
      }

      setLoadingDetail(true);
      try {
        const res = await formGroupService.formGroupDetail(token, id);
        if (res.data) {
          setName(res.data.name || '');
          setDescription(res.data.description || '');
          if (res.data.children && res.data.children.length > 0) {
            setChildren(
              res.data.children.map((c, idx) => ({
                tempId: `item-${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 7)}`,
                childId: c.childId || c.id || '',
                alias: c.alias || '',
                sort: c.sort || idx + 1
              }))
            );
          }
        }
      } catch (err) {
        console.error('Failed to fetch Form Group detail:', err);
        alert('Failed to load Form Group detail');
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchDetail();
  }, [id, isEdit]);

  const handleAddChild = () => {
    setChildren(prev => [
      ...prev,
      {
        tempId: `item-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        childId: '',
        alias: '',
        sort: prev.length + 1
      }
    ]);
  };

  const handleRemoveChild = (index: number) => {
    setChildren(prev => {
      const updated = prev.filter((_, idx) => idx !== index);
      return updated.map((item, idx) => ({ ...item, sort: idx + 1 }));
    });
  };

  const handleChildChange = (index: number, field: keyof ChildFormItem, value: any) => {
    setChildren(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setChildren(prev => {
        const oldIndex = prev.findIndex(item => item.tempId === active.id);
        const newIndex = prev.findIndex(item => item.tempId === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(prev, oldIndex, newIndex);
          return reordered.map((item, idx) => ({ ...item, sort: idx + 1 }));
        }
        return prev;
      });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('Please enter Form Group Name');
      return;
    }

    if (children.length === 0) {
      alert('Please add at least one child Form');
      return;
    }

    for (let i = 0; i < children.length; i++) {
      if (!children[i].childId) {
        alert(`Please select a Form for Item #${i + 1}`);
        return;
      }
    }

    const token = profile?.data?.token;
    if (!token) return;

    setSaving(true);
    try {
      const req: FormGroupSaveReq = {
        id: isEdit ? id : null,
        name: name.trim(),
        description: description.trim(),
        children: children.map((c, idx) => ({
          childId: c.childId,
          alias: c.alias.trim(),
          sort: idx + 1
        }))
      };

      await formGroupService.formGroupSave(token, req);
      alert('Form Group saved successfully!');
      navigate('/form');
    } catch (err: any) {
      console.error('Failed to save Form Group:', err);
      alert(typeof err === 'string' ? err : 'Failed to save Form Group');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12 mb-3">
          <div>
            <h5 className="mb-0 fw-bold" style={{ lineHeight: 1.2 }}>
              {isEdit ? 'Edit Form Group' : 'Create Form Group'}
            </h5>
            <small className="text-muted" style={{ fontSize: '0.78rem' }}>Manage group of forms and execution order</small>
          </div>
        </div>

        <div className="col-12">
          {isLoadingPage ? (
            <div className="p-5 text-center bg-white rounded shadow-sm">
              <div className="spinner-border text-primary mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="text-muted small">Loading Form Group data...</div>
            </div>
          ) : (
            <form onSubmit={handleSave}>
              {/* Form Group Details Card */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white py-3 border-bottom">
                  <h6 className="mb-0 fw-bold text-primary">
                    <i className="fa-solid fa-layer-group me-2"></i> Form Group Information
                  </h6>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Form Group Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Group Form Survey Outlet"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Kumpulan form survey toko dan audit POSM"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Children Card */}
              <div className="card border-0 shadow-sm mb-4" style={{ overflow: 'visible' }}>
                <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold text-primary">
                    <i className="fa-solid fa-list-check me-2"></i> Child Forms Sequence
                  </h6>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleAddChild}
                  >
                    <i className="fa-solid fa-plus me-1"></i> Add Child Form
                  </button>
                </div>
                <div className="card-body p-4" style={{ overflow: 'visible', minHeight: '260px', paddingBottom: '140px' }}>
                  {children.length === 0 ? (
                    <div className="text-center p-4 border border-dashed rounded-3 bg-light">
                      <i className="fa-solid fa-folder-open text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                      <p className="text-muted mb-2">No child forms added to this group yet.</p>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleAddChild}
                      >
                        <i className="fa-solid fa-plus me-1"></i> Add First Child Form
                      </button>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="table-responsive" style={{ overflow: 'visible', minHeight: '200px' }}>
                        <table className="table table-hover align-middle">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '50px' }}></th>
                              <th className="text-center" style={{ width: '50px' }}>#</th>
                              <th>Select Child Form <span className="text-danger">*</span></th>
                              <th>Stage Name / Alias</th>
                              <th className="text-center" style={{ width: '70px' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            <SortableContext
                              items={children.map(c => c.tempId)}
                              strategy={verticalListSortingStrategy}
                            >
                              {children.map((child, idx) => (
                                <SortableChildRow
                                  key={child.tempId}
                                  id={child.tempId}
                                  index={idx}
                                  child={child}
                                  availableForms={availableForms}
                                  onChildChange={handleChildChange}
                                  onRemoveChild={handleRemoveChild}
                                />
                              ))}
                            </SortableContext>
                          </tbody>
                        </table>
                      </div>
                    </DndContext>
                  )}
                </div>
              </div>

              {/* Action Footer */}
              <div className="d-flex justify-content-end align-items-center gap-2">
                <Link to="/form" className="btn btn-light px-4 mb-0 d-inline-flex align-items-center justify-content-center">
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary px-4 d-inline-flex align-items-center gap-2"
                  disabled={saving}
                >
                  {saving && <span className="spinner-border spinner-border-sm" role="status"></span>}
                  <i className="fa-solid fa-save"></i> Save Form Group
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormGroupFactory;
